import { endOfDay, isValid, parseISO, startOfDay } from 'date-fns';
import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { JOURNEY_DEFAULT_PERSON_ID, JOURNEY_QUERY_KEYS } from '../constants/journey';

interface DateRange {
  start: Date | null;
  end: Date | null;
}

function buildInitialDateRange(searchParams: URLSearchParams): DateRange {
  const startDateParam = searchParams.get(JOURNEY_QUERY_KEYS.startDate);
  const endDateParam = searchParams.get(JOURNEY_QUERY_KEYS.endDate);

  const parsedStart = startDateParam ? startOfDay(parseISO(startDateParam)) : null;
  const parsedEnd = endDateParam ? endOfDay(parseISO(endDateParam)) : null;

  if (parsedStart && parsedEnd && isValid(parsedStart) && isValid(parsedEnd) && parsedStart <= parsedEnd) {
    return { start: parsedStart, end: parsedEnd };
  }

  const today = new Date();
  return { start: startOfDay(today), end: endOfDay(today) };
}

const useJourneyQueryState = () => {
  const params = useParams<{ personId?: string }>();
  const [searchParams] = useSearchParams();

  const selectedPersonId = useMemo(() => {
    const fromParams = params.personId ? Number(params.personId) : NaN;
    if (Number.isFinite(fromParams)) return fromParams;

    const fromQuery = Number(searchParams.get(JOURNEY_QUERY_KEYS.personId));
    if (Number.isFinite(fromQuery)) return fromQuery;

    return JOURNEY_DEFAULT_PERSON_ID;
  }, [params.personId, searchParams]);

  const [dateRange, setDateRange] = useState<DateRange>(() => buildInitialDateRange(searchParams));

  return {
    selectedPersonId,
    dateRange,
    setDateRange,
  };
};

export default useJourneyQueryState;
